import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { cloneElement, FC, ReactElement, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { Account, Address, isThor } from "rujira.js";
import {
  AccountProvider,
  Button,
  Input,
  Provider,
  Spinner,
  useLocalStorage,
} from "../..";
import combined from "../../assets/images/combined.png";
import { useGlobalModalContext } from "../../context/GlobalModal";
import { i18n } from "../../i18n";
import { Eye, EyeSlash, LinkDisconnect, Plus, Users } from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";
import { Fiat } from "../numbers/Fiat";
import { ResolveLink } from "./ResolveLink";
import connect from "../../assets/images/connect.gif";
import connectStatic from "../../assets/images/connect.png";

let referral = localStorage.getItem("referral");

interface AccountsProps {
  provider: AccountProvider;
  routingElement: any;
  wallets: WalletProps[];
  ProviderIcon: FC<{
    provider: Provider.Key;
    selected: boolean;
    className?: string;
  }>;
  getValue: (account: Account) => bigint;
  customActions?: ReactElement[];
  isLoading?: boolean;
}

export const Accounts = ({
  provider,
  routingElement,
  wallets,
  ProviderIcon,
  getValue,
  customActions,
}: AccountsProps): ReactElement => {
  const { accounts: allAccounts, disconnectAll, selected } = provider;
  const [showBlur, setShowBlur] = useState(false);
  const { hideModal, showModal } = useGlobalModalContext();
  const [showBalance, setShowBalance] = useLocalStorage<string>(
    "rujira-show-balance",
    "true"
  );

  const accounts = allAccounts?.filter((a) => isThor(a.address));

  const ReferralModal = () => {
    showModal({
      title: "Refer And Earn",
      backgroundClose: true,
      children: (
        <>
          <p className="p balance">
            Share your unique referral link and earn affiliate fees on trades,
            swaps, and all DeFi interactions.
          </p>
          <div
            className="pointer"
            onClick={() => {
              toast.success("referral copied to clipboard");
              navigator.clipboard.writeText(
                `https://rujira.network/?ref=${referral}`
              );
            }}>
            <Input
              value={`https://rujira.network/?ref=${referral}`}
              readOnly={true}
              label="Click to copy"
            />
          </div>
          <p className="fs-12 color-grey mt-2">
            This referral link is valid until ...
          </p>
          <div className="modal__footer mt-4 px-3 py-2 text-right">
            <Button label="Close" onClick={hideModal} />
          </div>
        </>
      ),
    });
  };

  return (
    <>
      {!accounts || accounts.length === 0 ? (
        <ConnectAnimation>
          <Wallets
            provider={provider}
            routingElement={routingElement}
            wallets={wallets}
          />
        </ConnectAnimation>
      ) : (
        <MyAccount
          routingElement={routingElement}
          provider={provider}
          value={
            selected
              ? getValue({
                  address: selected.address,
                  network: "THOR",
                })
              : false
          }
          onHide={() => setShowBlur(false)}>
          {selected ? (
            <ResolveLink
              as={routingElement}
              to="/portfolio"
              className="balance">
              <div className="flex ai-b">
                {showBalance === "true" ? (
                  <>
                    <Fiat
                      amount={getValue({
                        address: selected.address,
                        network: "THOR",
                      })}
                      decimals={12}
                      symbol="~$"
                      padSymbol={false}
                      className="color-white fs-26 condensed"
                    />
                    <button
                      className="transparent ml-1 color-grey hover-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowBalance("false");
                        return false;
                      }}
                      style={{ marginRight: "-1rem" }}>
                      <Eye className="w-2 h-2" />
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      className="fiat color-grey fs-22"
                      style={{ fontFamily: "monospace" }}>
                      ****
                    </span>
                    <button
                      className="transparent ml-1 color-grey hover-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowBalance("true");
                        return false;
                      }}
                      style={{ marginRight: "-1rem" }}>
                      <EyeSlash className="w-2 h-2" />
                    </button>
                  </>
                )}
              </div>
              <p className="fs-12 condensed color-grey mt-0.5">
                {i18n.t("View portfolio")}
              </p>
            </ResolveLink>
          ) : null}

          {accounts.map((account) => (
            <AddressC
              key={account.address + account.provider}
              provider={provider}
              ProviderIcon={ProviderIcon}
              account={account}
              selected={
                selected?.address === account.address &&
                selected.provider === account.provider
              }
            />
          ))}

          <nav className="actions">
            {referral && (
              <button className="action transparent" onClick={ReferralModal}>
                <Users />
                <span>Referral Link</span>
              </button>
            )}
            <Link className="action transparent" to="/connect">
              <Plus />
              <span>Connect another wallet</span>
            </Link>
            {customActions &&
              customActions.map((e, index) => cloneElement(e, { key: index }))}
            <button className="action transparent" onClick={disconnectAll}>
              <LinkDisconnect />
              <span>Disconnect all</span>
            </button>
          </nav>
          {showBlur && <div className="blur" />}
        </MyAccount>
      )}
    </>
  );
};

const ConnectAnimation = ({ children }: { children: React.ReactNode }) => {
  const [show, setShow] = useState(false);

  return (
    <div
      className="relative py-q1 no-underline"
      onMouseOver={() => setShow(true)}
      onMouseOut={() => setShow(false)}>
      <div className="rujira-header__connect">
        <div className="fs-14 fw-500 fs-15 condensed">{i18n.t("Connect")}</div>
        <img
          className={"w-3 h-3 filter-white"}
          src={show ? connect : connectStatic}
          alt="Connect"
        />
      </div>
      <AnimatePresence>
        {show && (
          <motion.div
            className="rujira-header__popup right condensed w-32 text-center p-2"
            initial={{ opacity: 0, marginTop: -4 }}
            animate={{ opacity: 1, marginTop: 4 }}
            exit={{ opacity: 0, marginTop: -4 }}>
            <i18n.p className="color-white">Connect Your Wallet</i18n.p>
            <i18n.small className="color-grey mb-1">
              Create or connect an existing wallet
            </i18n.small>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export interface WalletProps {
  key: string;
  label: string;
  provider?: Provider.Key;
  icon: ReactElement;
  url?: string;
}

interface WalletsProps {
  modalLayout?: boolean;
  provider: AccountProvider;
  routingElement?: any;
  wallets: WalletProps[];
}

export const Wallets = ({
  modalLayout = false,
  provider,
  routingElement,
  wallets,
}: WalletsProps): ReactElement => {
  const { connect, isAvaialable } = provider;
  const { hideModal } = useGlobalModalContext();

  const [first, ...rest] = wallets;
  return (
    <>
      <div className="rujira-header__wallets">
        <div
          className={clsx({
            "flex w-full ai-c jc-c wrap": true,
            "mb-1": modalLayout,
          })}>
          <button
            id="connect-station"
            data-tooltip-content="Coming Soon..."
            data-tooltip-id="wallet-tip"
            className={clsx({
              "transparent block pointer big": true,
              "desaturate opacity-10": !first?.provider,
            })}
            disabled={!first?.provider}
            onClick={() => {
              first?.provider && connect(first?.provider).then(hideModal);
            }}>
            {first?.icon}
          </button>
        </div>
        <div className="flex w-full ai-c jc-c wrap">
          {rest.map((x) => {
            const disabled = x.provider ? !isAvaialable(x.provider) : true;
            return (
              <button
                key={x.label}
                id={`connect-${x.label}`}
                data-tooltip-content={x.label}
                data-tooltip-id="wallet-tip"
                className={clsx({
                  "transparent block pointer": true,
                  medium: modalLayout,
                  "desaturate opacity-10": disabled,
                })}
                onClick={() => {
                  if (disabled && x.url) {
                    window.open(x.url, "_blank");
                    return;
                  }
                  x.provider && connect(x.provider).then(hideModal);
                }}>
                {x.icon}
              </button>
            );
          })}
        </div>

        <hr
          className={clsx({
            hr: true,
            "my-0.5": !modalLayout,
            "my-2": modalLayout,
          })}
        />
        <ResolveLink
          as={routingElement}
          to="/ecosystem/wallets"
          onClick={hideModal}>
          Need help deciding?
        </ResolveLink>
      </div>
      <Tooltip id="wallet-tip" className="tooltip" />
    </>
  );
};

const MyAccount = ({
  provider,
  children,
  onHide,
  routingElement,
  value,
}: {
  provider: AccountProvider;
  children: React.ReactNode;
  onHide: () => void;
  routingElement?: any;
  value: bigint | false;
}) => {
  const [show, setShow] = useState(false);
  const { accounts, selected } = provider;
  const [showBalance] = useLocalStorage<string>("rujira-show-balance", "true");

  useEffect(() => {
    onHide();
  }, [show]);

  return (
    <div
      className="relative py-q1"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <ResolveLink
        as={routingElement}
        to="/portfolio"
        className="rujira-header__pfp">
        {value !== false && showBalance === "true" && (
          <Fiat amount={value} symbol="~$" decimals={12} padSymbol={false} />
        )}
        {selected && <NetworkIcon network={"THOR"} />}
        {accounts?.length && !selected && <img src={combined} alt="combined" />}
        {provider.isLoading && <Spinner />}
      </ResolveLink>
      <AnimatePresence>
        {show && (
          <motion.div
            className="rujira-header__popup right condensed p-1.5 br-2"
            initial={{ opacity: 0, marginTop: -4, right: -8 }}
            animate={{ opacity: 1, marginTop: 4, right: -8 }}
            exit={{ opacity: 0, marginTop: -4, right: -8 }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface AddressProps {
  account: { address: Address; provider: Provider.Key };
  selected: boolean;
  provider: AccountProvider;
  ProviderIcon: FC<{
    provider: Provider.Key;
    selected: boolean;
    className?: string;
  }>;
}

const AddressC: FC<AddressProps> = ({
  account,
  selected,
  provider,
  ProviderIcon,
}) => {
  const { select, disconnect } = provider;
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      className={clsx({
        "flex ai-c px-1 address": true,
        "address--selected": selected,
      })}>
      <NetworkIcon
        network={"THOR"}
        className="address__wallet"
        selected={selected || hover}
      />
      {/* <ProviderIcon provider={account.provider} selected={selected} /> */}
      <p
        className="address__address pointer"
        onClick={() => {
          if (selected) {
            toast.success("address copied to clipboard");
            navigator.clipboard.writeText(account.address);
          } else {
            select(account);
          }
        }}>
        {hover && selected ? (
          "copy address..."
        ) : (
          <>
            {account.address.substring(0, 8)} ... {account.address.slice(-8)}
          </>
        )}
      </p>

      {hover ? (
        <LinkDisconnect
          className="address__disconnect"
          onClick={() => disconnect(account.provider)}
        />
      ) : (
        <ProviderIcon
          className="address__disconnect"
          provider={account.provider}
          selected={selected}
        />
      )}
    </div>
  );
};
